import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { branchTablesAPI, getApiErrorMessage, heldOrdersAPI, receiptsAPI } from '@matix/api-client';
import type { BranchTableDto } from '@matix/api-client';
import { POS_PERMISSIONS, type ReceiptDetail } from '@matix/types';
import { useUI } from '../../store/ui';
import { useCart } from '../../store/cart';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { useCanI } from '../../lib/permissions';
import { XIcon } from '../../components/icons';
import { reloadAfterTableAction, tableActionErrorText } from './tableActions';
import { useReprintReceipt } from '../print/useReprintReceipt';

type Mode = 'separate' | 'transfer';

/**
 * The legacy till's Separate / Transfer dialog, for a HELD dine-in order.
 *
 *   Separate — the ticked items (or part of a line's quantity) become a NEW
 *              order on the same table: separate checks for one party.
 *   Transfer — the ticked items go to another table: onto the order already
 *              there, or a new order when the table is free.
 *
 * It lists the order AS SAVED (the server's lines, with their ids), which is
 * why the cart must be held first — the caller checks that before opening.
 * Each mode is behind its own legacy permission; the server checks too.
 *
 * Separate also offers "Split every item" (each line its own bill, same
 * table, a new invoice number each) and, for both, prints every resulting
 * bill — this order and the new one(s) — when "Print the bills" is ticked.
 */
export function SplitTransferModal() {
  const isOpen = useUI((s) => s.openModal === 'splitTransfer');
  const closeModal = useUI((s) => s.closeModal);
  const receiptId = useCart((s) => s.editingReceiptId);
  const { t: tr, lang } = useLang();
  const canSeparate = useCanI(POS_PERMISSIONS.SeparateTableOrders);
  const canTransfer = useCanI(POS_PERMISSIONS.TransferTableItems);
  const queryClient = useQueryClient();
  const reprint = useReprintReceipt();
  /** Print this order and the new one(s) after a Separate. */
  const [printBills, setPrintBills] = useState(true);

  const [mode, setMode] = useState<Mode>('separate');
  /** line id → quantity to move (absent = not ticked). */
  const [picked, setPicked] = useState<Record<number, number>>({});
  const [targetTable, setTargetTable] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const detailQuery = useQuery<ReceiptDetail>({
    queryKey: ['held-split', receiptId],
    queryFn: () => receiptsAPI.getById(receiptId!),
    enabled: isOpen && receiptId != null,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const tablesQuery = useQuery<BranchTableDto[]>({
    queryKey: ['branch', 'tables', 'all'],
    queryFn: () => branchTablesAPI.listTables(null),
    enabled: isOpen && mode === 'transfer',
    staleTime: 0,
  });

  useEffect(() => {
    if (isOpen) {
      setMode(canSeparate ? 'separate' : 'transfer');
      setPicked({});
      setTargetTable(null);
    }
  }, [isOpen, canSeparate]);

  const lines = useMemo(
    () => (detailQuery.data?.lines ?? []).filter((l) => !l.is_return && l.qty > 0),
    [detailQuery.data],
  );
  const ownTables = useMemo(() => {
    const own = new Set<number>();
    for (const t of tablesQuery.data ?? []) {
      if ((t.held_orders ?? []).some((o) => o.receipt_id === receiptId)) own.add(t.id);
    }
    if (detailQuery.data?.branch_table_id != null) own.add(detailQuery.data.branch_table_id);
    return own;
  }, [tablesQuery.data, detailQuery.data, receiptId]);

  if (!isOpen || receiptId == null) return null;

  const chosen = Object.entries(picked)
    .map(([id, qty]) => ({ line_id: Number(id), qty }))
    .filter((l) => l.qty > 0);
  const takesEverything =
    lines.length > 0 && lines.every((l) => (picked[l.id] ?? 0) >= l.qty - 1e-9);
  const canSave =
    !busy &&
    chosen.length > 0 &&
    (mode === 'separate' ? !takesEverything : targetTable != null);

  function toggle(lineId: number, full: number) {
    setPicked((p) => {
      const next = { ...p };
      if (next[lineId] != null) delete next[lineId];
      else next[lineId] = full;
      return next;
    });
  }

  function setQty(lineId: number, raw: string, max: number, decimals: boolean) {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    const v = Math.min(max, Math.max(0, decimals ? Math.round(n * 1000) / 1000 : Math.floor(n)));
    setPicked((p) => ({ ...p, [lineId]: v }));
  }

  function tableLabel(t: BranchTableDto): string {
    if (t.name && t.name.trim()) return t.name.trim();
    return tr({ en: `Table ${t.table_number}`, ar: `طاولة ${t.table_number}` });
  }

  /** Print each bill in turn: this order first, then the new ones. */
  async function printAll(ids: number[]) {
    for (const id of ids) {
      if (!(await reprint(id))) break;
    }
  }

  async function splitEach() {
    if (busy || receiptId == null || lines.length < 2) return;
    const ok = window.confirm(
      tr({
        en: `Split all ${lines.length} items into ${lines.length} separate bills on this table?`,
        ar: `تقسيم كل الأصناف (${lines.length}) إلى ${lines.length} فواتير منفصلة على نفس الطاولة؟`,
      }),
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await heldOrdersAPI.separateEach(receiptId);
      await queryClient.invalidateQueries({ queryKey: ['branch', 'tables'] });
      const nums = (res.targets ?? []).map((t) => `#${t.entry_no}`).join(', ');
      toast(
        tr({ en: `Split into #${res.entry_no}, ${nums}`, ar: `تم التقسيم إلى #${res.entry_no}، ${nums}` }),
        'success',
      );
      closeModal();
      if (printBills) await printAll([res.order_id, ...(res.targets ?? []).map((t) => t.order_id)]);
      await reloadAfterTableAction(res);
    } catch (err) {
      toast(tableActionErrorText(err) ?? getApiErrorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!canSave || receiptId == null) return;
    setBusy(true);
    try {
      const res =
        mode === 'separate'
          ? await heldOrdersAPI.separate(receiptId, chosen)
          : await heldOrdersAPI.transferItems(receiptId, { lines: chosen, branch_table_id: targetTable! });
      await queryClient.invalidateQueries({ queryKey: ['branch', 'tables'] });
      const n = res.target?.entry_no;
      toast(
        mode === 'separate'
          ? tr({ en: `Separated into order #${n}`, ar: `تم الفصل في الطلب #${n}` })
          : res.target?.created
            ? tr({ en: `Moved to a new order #${n}`, ar: `تم النقل إلى طلب جديد #${n}` })
            : tr({ en: `Moved to order #${n}`, ar: `تم النقل إلى الطلب #${n}` }),
        'success',
      );
      closeModal();
      if (mode === 'separate' && printBills && res.target) await printAll([res.order_id, res.target.order_id]);
      await reloadAfterTableAction(res);
    } catch (err) {
      toast(tableActionErrorText(err) ?? getApiErrorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  const nameOf = (l: (typeof lines)[number]): string =>
    (lang === 'ar' ? l.item_name_ar || l.item_name : l.item_name || l.item_name_ar) ?? '';
  const targets = (tablesQuery.data ?? [])
    .filter((t) => !ownTables.has(t.id))
    .sort((a, b) => (a.table_number ?? 0) - (b.table_number ?? 0));

  return (
    <div className="modal-overlay show" onClick={closeModal}>
      <div className="modal split-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-img split-icon" aria-hidden="true">
            ⇄
          </div>
          <div className="modal-header-title">
            <div className="modal-header-name">
              {tr({ en: 'Separate / Transfer items', ar: 'فصل / نقل الأصناف' })}
            </div>
            <div className="modal-header-name-ar">
              {detailQuery.data
                ? tr({ en: `Order #${detailQuery.data.entry_no}`, ar: `الطلب #${detailQuery.data.entry_no}` })
                : ''}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={closeModal} aria-label={tr({ en: 'Close', ar: 'إغلاق' })}>
            <XIcon size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="split-modes" role="radiogroup">
            {canSeparate && (
              <label className={`split-mode${mode === 'separate' ? ' is-on' : ''}`}>
                <input type="radio" name="split-mode" checked={mode === 'separate'} onChange={() => setMode('separate')} />
                <span>
                  <b>{tr({ en: 'Separate', ar: 'فصل' })}</b>
                  <small>{tr({ en: 'A new bill on the same table', ar: 'فاتورة جديدة على نفس الطاولة' })}</small>
                </span>
              </label>
            )}
            {canTransfer && (
              <label className={`split-mode${mode === 'transfer' ? ' is-on' : ''}`}>
                <input type="radio" name="split-mode" checked={mode === 'transfer'} onChange={() => setMode('transfer')} />
                <span>
                  <b>{tr({ en: 'Transfer', ar: 'نقل' })}</b>
                  <small>{tr({ en: 'To another table', ar: 'إلى طاولة أخرى' })}</small>
                </span>
              </label>
            )}
          </div>

          {mode === 'separate' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, margin: '4px 0 10px' }}>
              <button
                type="button"
                className="btn-secondary-modal"
                onClick={() => void splitEach()}
                disabled={busy || lines.length < 2}
                title={tr({
                  en: 'Every item becomes its own bill on this table, each with a new invoice number',
                  ar: 'كل صنف يصبح فاتورة مستقلة على هذه الطاولة، لكل منها رقم فاتورة جديد',
                })}
              >
                {tr({ en: 'Split every item', ar: 'تقسيم كل صنف في فاتورة' })}
              </button>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input type="checkbox" checked={printBills} onChange={(e) => setPrintBills(e.target.checked)} />
                {tr({ en: 'Print the bills', ar: 'طباعة الفواتير' })}
              </label>
            </div>
          )}

          {detailQuery.isLoading ? (
            <div className="split-empty">{tr({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</div>
          ) : (
            <table className="split-table">
              <thead>
                <tr>
                  <th className="split-col" />
                  <th>{tr({ en: 'Item', ar: 'الصنف' })}</th>
                  <th className="num">{tr({ en: 'On order', ar: 'في الطلب' })}</th>
                  <th className="num">{tr({ en: 'Move', ar: 'ينقل' })}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const on = picked[l.id] != null;
                  return (
                    <tr key={l.id} className={on ? 'is-on' : undefined} onClick={() => toggle(l.id, l.qty)}>
                      <td className="split-col">
                        <input type="checkbox" checked={on} readOnly aria-label={nameOf(l)} />
                      </td>
                      <td>
                        <div className="split-name">{nameOf(l)}</div>
                        {l.notes && <div className="split-meta">{l.notes}</div>}
                      </td>
                      <td className="num">{fmtQty(l.qty)}</td>
                      <td className="num" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="number"
                          className="split-qty"
                          min={0}
                          max={l.qty}
                          step={l.allow_decimals ? 0.001 : 1}
                          disabled={!on}
                          value={on ? picked[l.id] : ''}
                          onChange={(e) => setQty(l.id, e.target.value, l.qty, l.allow_decimals)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {mode === 'transfer' && (
            <div className="split-targets">
              <div className="split-targets-title">{tr({ en: 'To table', ar: 'إلى الطاولة' })}</div>
              {tablesQuery.isLoading ? (
                <div className="split-empty">{tr({ en: 'Loading tables…', ar: 'جارٍ تحميل الطاولات…' })}</div>
              ) : (
                <div className="split-target-grid">
                  {targets.map((t) => {
                    const first = (t.held_orders ?? [])[0];
                    return (
                      <button
                        key={t.id}
                        type="button"
                        className={`split-target${targetTable === t.id ? ' is-on' : ''}${first ? ' is-taken' : ''}`}
                        onClick={() => setTargetTable(t.id)}
                      >
                        <span className="split-target-name">{tableLabel(t)}</span>
                        <span className="split-target-sub">
                          {first
                            ? tr({ en: `Order #${first.entry_no}`, ar: `الطلب #${first.entry_no}` })
                            : tr({ en: 'Free · new order', ar: 'متاحة · طلب جديد' })}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer split-footer">
          <div className="split-summary">
            {mode === 'separate' && takesEverything
              ? tr({ en: 'Leave at least one item on this order.', ar: 'اترك صنفاً واحداً على الأقل في هذا الطلب.' })
              : mode === 'transfer' && takesEverything
                ? tr({ en: 'Every item moves — this order will be closed.', ar: 'ستُنقل كل الأصناف — سيُغلق هذا الطلب.' })
                : ''}
          </div>
          <button type="button" className="btn-secondary-modal" onClick={closeModal} disabled={busy}>
            {tr({ en: 'Cancel', ar: 'إلغاء' })}
          </button>
          <button type="button" className="btn-primary-modal" onClick={() => void save()} disabled={!canSave}>
            {busy ? tr({ en: 'Saving…', ar: 'جارٍ الحفظ…' }) : tr({ en: 'Save', ar: 'حفظ' })}
          </button>
        </div>
      </div>
    </div>
  );
}

function fmtQty(n: number): string {
  return Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 3 });
}
